import { useState, useEffect } from 'react'
import { Calendar, TrendingUp, BarChart3 } from 'lucide-react'
import MetricCard from '../ui/MetricCard'
import LoadingSpinner from '../ui/LoadingSpinner'
import ErrorMessage from '../ui/ErrorMessage'
import { apiService } from '../../services/api'
import { debug } from '../../utils/debug'
import { useUser } from '../../context/UserContext'
import { ReportShell, FilterBar } from '../report-shell'

const HistoryOverview = () => {
  const { getAccessibleOperators } = useUser()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [operatorStatus, setOperatorStatus] = useState([])
  const [rules, setRules] = useState(null)
  
  // Month/Year selector state - default to previous month
  const currentDate = new Date()
  const prevMonth = currentDate.getMonth() // 0-11, so this is already "previous month" in 1-12 terms
  const [selectedMonth, setSelectedMonth] = useState(prevMonth === 0 ? 12 : prevMonth) // Handle January -> December
  const [selectedYear, setSelectedYear] = useState(prevMonth === 0 ? currentDate.getFullYear() - 1 : currentDate.getFullYear())
  
  const months = [
    { value: 1, label: 'Styczeń' },
    { value: 2, label: 'Luty' },
    { value: 3, label: 'Marzec' },
    { value: 4, label: 'Kwiecień' },
    { value: 5, label: 'Maj' },
    { value: 6, label: 'Czerwiec' },
    { value: 7, label: 'Lipiec' },
    { value: 8, label: 'Sierpień' },
    { value: 9, label: 'Wrzesień' },
    { value: 10, label: 'Październik' },
    { value: 11, label: 'Listopad' },
    { value: 12, label: 'Grudzień' }
  ]
  
  const currentYear = new Date().getFullYear()
  const years = Array.from({ length: currentYear - 2024 + 1 }, (_, i) => 2024 + i)

  useEffect(() => {
    const fetchHistoricalData = async () => {
      debug.component('HistoryOverview', `Fetching data for ${selectedMonth}/${selectedYear}`)
      debug.time('History Data Fetch')
      
      setLoading(true)
      setError(null)
      
      try {
        // Fetch historical data with month/year parameters
        const [salesGoalsResponse, rulesResponse] = await Promise.all([
          apiService.getSalesGoalsV2({ month: selectedMonth, year: selectedYear }).catch((err) => {
            debug.warn('Historical sales goals fetch failed', err, 'HistoryOverview')
            return { data: {} }
          }),
          apiService.getRules().catch((err) => {
            debug.warn('Rules fetch failed', err, 'HistoryOverview')
            return { data: { operators: {} } }
          })
        ])
        
        const salesGoals = salesGoalsResponse.data || {}
        const rulesData = rulesResponse.data || { operators: {} }
        
        setRules(rulesData)
        
        // Define desired operator order: T-Mobile, Orange, Plus, Play
        const allOperatorOrder = ['TMOBILE', 'ORANGE', 'PLUS', 'P4']
        
        // Filter operators based on user access
        const accessibleOperatorCodes = getAccessibleOperators(allOperatorOrder)
        const operatorOrder = allOperatorOrder.filter(code => accessibleOperatorCodes.includes(code))
        
        // Transform operator data for history view
        const transformedOperators = operatorOrder.map(operatorCode => {
          const goalData = salesGoals[operatorCode] || {}
          const operatorConfig = rulesData.operators?.[operatorCode] || {}
          
          // Get MTD achievement data
          const actualMtd = goalData.actual_sales_mtd || 0
          const goalValue = goalData.goal_value || 0
          const achievementPct = goalData.achievement_percent || 0
          const forecastPct = goalData.forecast_vs_goal_percent
          
          // Determine operator status based on achievement (no forecast in history)
          let status = 'medium'
          if (achievementPct >= 100) {
            status = 'good'
          } else if (achievementPct >= 80) {
            status = 'medium'
          } else if (achievementPct >= 50) {
            status = 'medium'
          } else {
            status = 'bad'
          }
          
          // Check if operator has sales data
          const hasSalesData = operatorConfig.has_sales_data !== false
          if (!hasSalesData) {
            status = 'na'
          }
          
          return {
            name: getOperatorName(operatorCode),
            code: operatorCode,
            status: status,
            color: getOperatorColor(operatorCode),
            // Historical metrics
            actualSales: Math.round(actualMtd),
            goalValue: Math.round(goalValue),
            achievementPct: achievementPct ? achievementPct.toFixed(1) : '0.0',
            forecastPct: forecastPct ? forecastPct.toFixed(1) : null,
            forecasted: goalData.forecasted_sales ? Math.round(goalData.forecasted_sales) : null,
            hasSalesData: hasSalesData,
            unit: (operatorCode === 'TMOBILE' || operatorCode === 'P4') ? 'akt.' : 'szt.',
            salesBreakdown: goalData.sales_breakdown || null,
          }
        })

        debug.log('Historical Sales Goals Data', salesGoals, 'HistoryOverview')
        debug.log('Transformed historical operators', transformedOperators, 'HistoryOverview')
        setOperatorStatus(transformedOperators)
        debug.timeEnd('History Data Fetch')
        
      } catch (err) {
        debug.error('Error fetching historical data', err, 'HistoryOverview')
        setError('Nie udało się załadować danych historycznych. Sprawdź połączenie z API.')
      } finally {
        setLoading(false)
      }
    }

    fetchHistoricalData()
  }, [selectedMonth, selectedYear])

  const getOperatorName = (code) => {
    const mapping = {
      'ORANGE': 'Orange',
      'TMOBILE': 'T-Mobile',
      'PLUS': 'Plus (PLK)',
      'P4': 'Play'
    }
    return mapping[code] || code
  }

  const getOperatorColor = (code) => {
    const colorMap = {
      'ORANGE': 'orange',
      'TMOBILE': 'tmobile',
      'PLUS': 'plus',
      'P4': 'play'
    }
    return colorMap[code] || 'gray'
  }

  const getStatusBadgeClass = (status) => {
    if (status === 'good') return 'bg-green-100 text-green-800'
    if (status === 'medium') return 'bg-yellow-100 text-yellow-800'
    if (status === 'bad') return 'bg-red-100 text-red-800'
    if (status === 'na') return 'bg-blue-100 text-blue-800'
    return 'bg-gray-100 text-gray-800'
  }

  const getStatusText = (status) => {
    if (status === 'good') return 'CEL OSIĄGNIĘTY'
    if (status === 'medium') return 'ŚREDNIO'
    if (status === 'bad') return 'PONIŻEJ CELU'
    if (status === 'na') return 'BRAK DANYCH'
    return 'NIEZNANY'
  }

  const getPerformanceIcon = (operator) => {
    if (!operator.hasSalesData) return '📊'
    if (operator.status === 'good') return '🎯'
    if (operator.status === 'medium') return '📈'
    if (operator.status === 'bad') return '⚠️'
    return '📊'
  }

  const getPerformanceText = (operator) => {
    if (!operator.hasSalesData) {
      return `Brak danych sprzedażowych dla ${operator.name}`
    }
    
    // In history view, always show actual achievement (no forecast)
    return `Realizacja: ${operator.actualSales}/${operator.goalValue} ${operator.unit} (${operator.achievementPct}%)`
  }

  const periods = [
    {
      type: 'select', key: 'month', label: 'Miesiąc',
      value: selectedMonth, display: months.find(m => m.value === selectedMonth)?.label,
      options: months.map(m => ({ value: m.value, label: m.label })),
      onChange: (v) => setSelectedMonth(parseInt(v)),
      allowClear: false,
    },
    {
      type: 'select', key: 'year', label: 'Rok',
      value: selectedYear, display: String(selectedYear),
      options: years.map(y => ({ value: y, label: String(y) })),
      onChange: (v) => setSelectedYear(parseInt(v)),
      allowClear: false,
    },
  ]

  return (
    <ReportShell
      title="Historia wyników"
      subtitle="Trendy sprzedaży i KPI w czasie — historia per operator i KAM."
      breadcrumb={['KAM-SFA', 'Historia']}
      filterBar={<FilterBar periods={periods} />}
    >
      {loading && (
        <div className="flex items-center justify-center min-h-96">
          <LoadingSpinner size="xl" />
        </div>
      )}
      {error && !loading && (
        <ErrorMessage
          message={error}
          onRetry={() => { setError(null); window.location.reload() }}
        />
      )}
      {!loading && !error && (
    <div className="space-y-6">
      {/* Performance Banner */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {operatorStatus.map((operator) => {
          const getBannerStyle = () => {
            switch (operator.status) {
              case 'good':
                return 'bg-green-50 border-green-200 text-green-800'
              case 'medium':
                return 'bg-yellow-50 border-yellow-200 text-yellow-800'
              case 'bad':
                return 'bg-red-50 border-red-200 text-red-800'
              case 'na':
                return 'bg-blue-50 border-blue-200 text-blue-800'
              default:
                return 'bg-gray-50 border-gray-200 text-gray-800'
            }
          }

          return (
            <div key={operator.code} className={`p-4 rounded-lg border ${getBannerStyle()}`}>
              <div className="space-y-1">
                <div className="font-semibold" style={{ color: 
                  operator.color === 'orange' ? '#FF7900' :
                  operator.color === 'tmobile' ? '#E20074' :
                  operator.color === 'plus' ? '#00A651' :
                  operator.color === 'play' ? '#9333EA' : '#6b7280'
                }}>
                  {operator.name}:
                </div>
                <div className="font-medium">
                  {getPerformanceIcon(operator)} <strong>{getPerformanceText(operator)}</strong>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Detailed Operator Status Cards */}
      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <BarChart3 className="h-5 w-5 text-gray-600" />
          <h3 className="text-lg font-semibold text-gray-900">
            📊 Szczegóły realizacji celów - {months.find(m => m.value === selectedMonth)?.label} {selectedYear}
          </h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {operatorStatus.map((operator) => (
            <div key={operator.code} className="bg-white rounded-lg border border-gray-200 p-4 min-h-[320px] flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <span
                  className="px-3 py-1 rounded-full text-sm font-medium text-white"
                  style={{ backgroundColor:
                    operator.color === 'orange' ? '#FF7900' :
                    operator.color === 'tmobile' ? '#E20074' :
                    operator.color === 'plus' ? '#00A651' :
                    operator.color === 'play' ? '#9333EA' : '#6b7280'
                  }}
                >
                  {operator.name}
                </span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(operator.status)}`}>
                  {getStatusText(operator.status)}
                </span>
              </div>
              
              {/* Metrics */}
              {operator.hasSalesData ? (
                <>
                  {/* Sales + Goal */}
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                      <div className="text-xs text-gray-500 mb-1">Sprzedaż</div>
                      <div className="text-xl font-bold text-gray-900">{operator.actualSales}</div>
                      <div className="text-xs text-gray-400">{operator.unit}</div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3 text-center">
                      <div className="text-xs text-gray-500 mb-1">Cel</div>
                      <div className="text-xl font-bold text-gray-900">{operator.goalValue}</div>
                      <div className="text-xs text-gray-400">{operator.unit}</div>
                    </div>
                  </div>

                  {/* Aggregate progress bar */}
                  {operator.goalValue > 0 && (() => {
                    const pct = Math.min(parseFloat(operator.achievementPct), 100)
                    const color = pct >= 100 ? '#16a34a' : pct >= 80 ? '#f59e0b' : pct >= 60 ? '#f97316' : '#ef4444'
                    return (
                      <div className="mb-3">
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-gray-500">Realizacja</span>
                          <span className="font-semibold" style={{ color }}>{operator.achievementPct}%</span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-2.5">
                          <div className="h-2.5 rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
                        </div>
                        <div className="text-xs text-gray-400 mt-0.5">
                          {operator.actualSales} / {operator.goalValue} {operator.unit}
                        </div>
                      </div>
                    )
                  })()}

                  {/* Per-product-group breakdown with mini bars */}
                  {operator.salesBreakdown && Object.keys(operator.salesBreakdown).length > 0 && (
                    <div className="pt-3 border-t border-gray-100 space-y-2">
                      <div className="text-xs text-gray-500 font-medium">Per grupa produktów</div>
                      {Object.entries(operator.salesBreakdown).map(([name, data]) => {
                        const mtd = typeof data === 'object' ? (data.mtd ?? 0) : data
                        const goal = typeof data === 'object' ? data.goal : null
                        const pct = goal > 0 ? Math.min((mtd / goal) * 100, 100) : 0
                        const color = goal == null ? '#9ca3af' : pct >= 80 ? '#f59e0b' : pct >= 60 ? '#f97316' : '#ef4444'
                        return (
                          <div key={name}>
                            <div className="flex justify-between text-xs mb-0.5">
                              <span className="text-gray-600">{name}</span>
                              <span className="font-medium" style={{ color: goal != null ? color : '#374151' }}>
                                {Math.round(mtd)} {operator.unit}
                                {goal != null && <span className="text-gray-400 font-normal"> / {Math.round(goal)} ({pct.toFixed(0)}%)</span>}
                                {typeof data === 'object' && data.margin != null && (
                                  <span className="text-green-600 font-normal ml-1">{data.margin.toLocaleString('pl-PL')} PLN</span>
                                )}
                              </span>
                            </div>
                            {goal != null && (
                              <div className="w-full bg-gray-100 rounded-full h-1.5">
                                <div className="h-1.5 rounded-full" style={{ width: `${pct}%`, backgroundColor: color }} />
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )}
                </>
              ) : (
                <div className="flex items-center justify-center flex-grow">
                  <div className="text-center text-gray-500">
                    <div className="text-4xl mb-2">📊</div>
                    <div className="text-sm">Brak danych sprzedażowych</div>
                    <div className="text-xs mt-1">dla tego operatora</div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
      )}
    </ReportShell>
  )
}

export default HistoryOverview


